import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowDown, Music, Mic2, Newspaper, Camera, Heart, BookOpen, Frame, Plane } from "lucide-react";
import { api, resolveImg } from "../lib/api";
import Seo from "../components/Seo";

const QUOTE = "Mi propósito es ayudar a desarrollar esa voz del artista y contribuir a la conexión con su público.";
// Words to highlight in magenta when typewriter reveals them
const QUOTE_HIGHLIGHTS = ["voz", "artista", "público"];

// Render the partial typed text with the highlight words colored in magenta whenever
// they have been fully typed already.
function renderHighlighted(typed) {
  // Split preserving whitespace and punctuation; we color only the bare word match.
  const parts = typed.split(/(\s+|[.,;:])/);
  return parts.map((tok, i) => {
    const bare = tok.replace(/[.,;:]/g, "").toLowerCase();
    if (QUOTE_HIGHLIGHTS.includes(bare)) {
      return (
        <span key={i} className="text-[var(--magenta)]">{tok}</span>
      );
    }
    return <span key={i}>{tok}</span>;
  });
}

const FILTERS = [
  { key: "ALL",      label: "TODO",     cat: null,        count: null },
  { key: "MUSICA",   label: "Música",   cat: "musica",    count: null },
  { key: "EVENTOS",  label: "Eventos",  cat: "eventos",   count: null },
  { key: "VIAJES",   label: "Viajes",   cat: "viajes",    count: null },
  { key: "RETRATOS", label: "Retratos", cat: "retratos",  count: null },
];

/* ──────────────────────────────────────────── COLD OPEN */
function ColdOpen({ onEnter }) {
  const [text, setText] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i++;
      setText(QUOTE.slice(0, i));
      if (i >= QUOTE.length) {
        clearInterval(t);
        setTimeout(() => setDone(true), 1200);
      }
    }, 45);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!done) return;
    const onKey = (e) => { if (e.key === "Enter" || e.key === " ") onEnter(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [done, onEnter]);

  return (
    <motion.div
      data-testid="cold-open"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
      className="fixed inset-0 z-[70] bg-black flex flex-col items-center justify-center px-6"
    >
      <p className="font-mono text-[10px] sm:text-xs tracking-[0.4em] text-white/40 mb-12">
        EUPEGUI · COLD OPEN · 2026
      </p>
      <p className="max-w-5xl text-center font-serif-accent text-white text-2xl sm:text-4xl lg:text-[2.9rem] leading-[1.22] font-light tracking-tight">
        {renderHighlighted(text)}
        <span className="inline-block -mb-1 h-[1em] w-[2px] bg-[var(--magenta)] ml-1 animate-pulse align-middle" />
      </p>
      {done && (
        <motion.button
          data-testid="cold-open-enter"
          onClick={onEnter}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-16 group flex flex-col items-center gap-3 text-white/70 hover:text-white"
        >
          <span className="font-mono text-[10px] tracking-[0.5em]">ENTRAR · ENTER</span>
          <ArrowDown size={16} className="group-hover:translate-y-1 transition-transform" />
        </motion.button>
      )}
    </motion.div>
  );
}

/* ──────────────────────────────────────────── CONTACT SHEET (large frames) — DESKTOP ONLY */
function ContactSheet({ projects }) {
  const [filter, setFilter] = useState("ALL");
  const [hoveredSlug, setHoveredSlug] = useState(null);

  // ALL view = top 10 destacadas (curated home preview).
  // Category filters show the full set for that category.
  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    if (!f || !f.cat) return projects.slice(0, 10);
    return projects.filter((p) => p.category === f.cat);
  }, [filter, projects]);

  const sectionRef = useRef(null);

  // Bigger frames — each takes ~78vw + 4vw margin = 82vw
  const FRAME_VW = 82;
  // +1 for the leading TitleCard which is also 82vw wide
  const totalVW = Math.max((filtered.length + 1) * FRAME_VW + 18, 100);
  const travelVW = Math.max(totalVW - 100, 0);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });
  const x = useTransform(scrollYProgress, [0, 1], ["0vw", `-${travelVW}vw`]);
  const progressPercent = useScrollProgress(sectionRef);

  // Tall enough scroll container — slower pacing for big images
  const containerHeightVH = 100 + (travelVW / 100) * 95;

  const accentByCategory = { musica: "var(--magenta)", eventos: "var(--tungsten)", viajes: "var(--cyan)", retratos: "var(--rose)" };

  // Smooth-scroll to past the pinned strip — invoked by the "Saltar archivo" button.
  const skipArchive = () => {
    if (!sectionRef.current) return;
    const top = sectionRef.current.offsetTop + sectionRef.current.offsetHeight;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <section
      ref={sectionRef}
      data-testid="contact-sheet"
      className="relative bg-black hidden md:block"
      style={{ height: `${containerHeightVH}vh` }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">

        {/* TOP HUD — only the small chip + filter (NO big H1 overlay anymore) */}
        <div className="absolute top-[7vh] sm:top-[8vh] left-0 right-0 px-6 sm:px-12 lg:px-24 sm:pl-[20vw] lg:pl-[18vw] flex items-start justify-between gap-6 z-30 pointer-events-none">
          <div className="pointer-events-auto bg-black/60 backdrop-blur-md border border-white/[0.06] px-4 py-2.5 inline-flex items-center gap-3 flex-wrap">
            <ApertureIcon size={14} />
            <p className="font-mono text-[10px] tracking-[0.4em] text-white/75">
              {filter === "ALL"
                ? `${String(filtered.length).padStart(2, "0")} SERIES DESTACADAS · DE ${String(projects.length).padStart(2, "0")}`
                : `${String(filtered.length).padStart(2, "0")} SERIES · ${FILTERS.find(f=>f.key===filter)?.label.toUpperCase()}`}
            </p>
            <button
              onClick={skipArchive}
              data-testid="skip-archive"
              title="Bajar al texto del fotógrafo"
              className="ml-1 inline-flex items-center gap-1.5 text-white/60 hover:text-[var(--magenta)] transition-colors group"
            >
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Saltar</span>
              <ArrowDown size={12} className="group-hover:translate-y-[2px] transition-transform" />
            </button>
          </div>

          {/* CATEGORY FILTER */}
          <div className="pointer-events-auto hidden sm:flex flex-col items-end gap-2 bg-black/60 backdrop-blur-md border border-white/[0.06] px-5 py-4 mt-[8vh]" data-testid="category-filter">
            <p className="font-mono text-[10px] tracking-[0.4em] text-white/55">FILTRAR</p>
            <div className="flex flex-col items-end gap-1.5">
              {FILTERS.map((f) => {
                const isActive = filter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    data-testid={`filter-${f.key.toLowerCase()}`}
                    className={`flex items-baseline gap-3 group transition-colors ${isActive ? "text-white" : "text-white/55 hover:text-white/90"}`}
                  >
                    <span className="font-display uppercase tracking-[0.22em] text-sm">{f.label}</span>
                    <span
                      className="block h-px transition-all"
                      style={{
                        backgroundColor: isActive ? "var(--magenta)" : "rgba(255,255,255,0.25)",
                        width: isActive ? 36 : 14,
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* STRIP */}
        <motion.div
          className="absolute top-0 left-0 h-full flex items-center pl-[10vw] pr-[10vw]"
          style={{ x, width: `${totalVW + 16}vw` }}
        >
          {/* TITLE CARD — first inline item; scrolls out as user advances */}
          <TitleCard filterLabel={filter === "ALL" ? "Archivo completo" : FILTERS.find(f=>f.key===filter)?.label} />

          {filtered.map((p, idx) => {
            const accent = accentByCategory[p.category] || "var(--magenta)";
            const isHover = hoveredSlug === p.slug;
            return (
              <Link
                to={`/proyecto/${p.slug}`}
                key={p.slug}
                onMouseEnter={() => setHoveredSlug(p.slug)}
                onMouseLeave={() => setHoveredSlug(null)}
                data-testid={`frame-${p.slug}`}
                className="group relative shrink-0 mx-[2vw] flex flex-col"
                style={{ width: "78vw", maxWidth: "78vw" }}
              >
                {/* Frame label top — minimal */}
                <div className="flex items-center justify-between font-mono text-[10px] sm:text-[11px] tracking-[0.3em] text-white/40 mb-3 px-1">
                  <span>{String(idx + 1).padStart(2, "0")} / {String(filtered.length).padStart(2, "0")}</span>
                  <span style={{ color: accent }}>{p.category.toUpperCase()}</span>
                </div>

                {/* IMAGE — large, photographic 3:2 */}
                <div
                  className="relative overflow-hidden bg-[var(--surface)]"
                  style={{ aspectRatio: "3 / 2", maxHeight: "68vh" }}
                >
                  <img
                    src={resolveImg(p.cover)}
                    alt={`${p.title} — fotografía de ${p.category} por Emmanuel Upegui`}
                    loading={idx === 0 ? "eager" : "lazy"}
                    decoding="async"
                    fetchPriority={idx === 0 ? "high" : "auto"}
                    className="absolute inset-0 w-full h-full object-cover transition-all duration-700"
                    style={{
                      filter: isHover ? "grayscale(0) contrast(1.05) saturate(1.05)" : "grayscale(1) contrast(1.08) brightness(0.95)",
                      transform: isHover ? "scale(1.02)" : "scale(1)",
                      objectPosition: `${p.cover_focus_x ?? 50}% ${p.cover_focus_y ?? 50}%`,
                    }}
                  />
                  <div
                    className="absolute left-0 top-0 bottom-0 w-[3px] origin-bottom transition-transform duration-700"
                    style={{
                      backgroundColor: accent,
                      transform: isHover ? "scaleY(1)" : "scaleY(0)",
                      boxShadow: isHover ? `0 0 30px ${accent}` : "none",
                    }}
                  />
                  <CornerTicks />
                </div>

                {/* Caption — title only, larger */}
                <div className="mt-5 px-1">
                  <p className="font-display uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[0.95] text-white group-hover:text-white">
                    {p.title}
                  </p>
                </div>
              </Link>
            );
          })}
        </motion.div>

        {/* BOTTOM HUD — only progress, no hover quote */}
        <div className="absolute bottom-[7vh] sm:bottom-[8vh] left-0 right-0 px-6 sm:px-12 lg:px-24 sm:pl-[16vw] z-30 pointer-events-none flex items-end justify-end gap-6">
          <div className="hidden sm:flex items-center gap-3 font-mono text-[10px] tracking-[0.35em] text-white/40">
            <span className="block w-12 h-px bg-white/20" />
            <span>{Math.round(progressPercent * 100)}%</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function CornerTicks() {
  const c = "absolute w-4 h-4 border-white/40 pointer-events-none";
  return (
    <>
      <div className={`${c} top-2 left-2 border-t border-l`} aria-hidden />
      <div className={`${c} top-2 right-2 border-t border-r`} aria-hidden />
      <div className={`${c} bottom-2 left-2 border-b border-l`} aria-hidden />
      <div className={`${c} bottom-2 right-2 border-b border-r`} aria-hidden />
    </>
  );
}

/* TitleCard — first inline item of the strip. Scrolls naturally with the carousel. */
function TitleCard({ filterLabel }) {
  return (
    <div
      data-testid="title-card"
      className="shrink-0 mx-[2vw] flex flex-col justify-center"
      style={{ width: "78vw", maxWidth: "78vw", height: "72vh" }}
    >
      <p className="font-mono text-[10px] sm:text-[11px] tracking-[0.4em] text-[var(--magenta)] mb-6">
        00 / ARCHIVO · {(filterLabel || "").toString().toUpperCase()}
      </p>
      <h1 className="font-display uppercase font-bold tracking-tighter leading-[0.88] text-white text-[7vw] sm:text-[6.2vw] lg:text-[5.8vw]">
        Fotografía
        <br />
        para
        <br />
        <span className="text-[var(--magenta)]">artistas</span>
        <br />
        y creadores.
      </h1>
      <div className="mt-12 flex items-end gap-1.5 h-10" aria-hidden>
        <Equalizer bars={28} maxHeight={40} amplitude={0.85} speed={1.4} />
      </div>
      <p className="mt-10 font-mono text-[10px] sm:text-[11px] tracking-[0.4em] text-white/40">
        ◀ DESPLAZA · SCROLL TO BEGIN ▶
      </p>
    </div>
  );
}

/* Animated camera aperture (diaphragm) — six blades that breathe open/closed.
   Replaces the old REC dot with something more photographic. */
function ApertureIcon({ size = 14 }) {
  return (
    <span
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
      aria-hidden
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 32 32"
        className="aperture-spin"
      >
        <g fill="none" stroke="var(--magenta)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          {/* Outer ring */}
          <circle cx="16" cy="16" r="13" opacity="0.55" />
          {/* Six blades forming the iris */}
          {Array.from({ length: 6 }).map((_, i) => {
            const a = (i * 60 * Math.PI) / 180;
            const x1 = 16 + Math.cos(a) * 12;
            const y1 = 16 + Math.sin(a) * 12;
            const x2 = 16 + Math.cos(a + Math.PI / 3) * 4.5;
            const y2 = 16 + Math.sin(a + Math.PI / 3) * 4.5;
            return <line key={`aperture-blade-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} />;
          })}
        </g>
      </svg>
      {/* Inner pulsing pupil to emulate the iris breathing */}
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="block rounded-full bg-[var(--magenta)] aperture-pulse" />
      </span>
    </span>
  );
}

/* Pulsing REC dot — kept for legacy use (Volume Outro) */
function RecDot() {
  return (
    <span className="inline-flex items-center gap-2" aria-hidden>
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--magenta)] opacity-70 animate-ping" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--magenta)]" />
      </span>
      <span className="font-mono text-[10px] tracking-[0.35em] text-[var(--magenta)]">REC</span>
    </span>
  );
}

/* Animated audio-style equalizer — heights pulse via requestAnimationFrame */
function Equalizer({ bars = 56, maxHeight = 88, amplitude = 1, speed = 1, className = "", color = "rgba(255,255,255,0.18)", hoverColor = "var(--magenta)" }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const spans = Array.from(el.querySelectorAll("span[data-bar='1']"));
    let raf;
    const start = performance.now();
    const phases = spans.map(() => Math.random() * Math.PI * 2);
    const speeds = spans.map(() => 0.6 + Math.random() * 0.7);

    const tick = (now) => {
      const t = ((now - start) / 1000) * speed;
      spans.forEach((s, i) => {
        const v = Math.abs(Math.sin(t * speeds[i] + phases[i]) * 0.6 + Math.cos(t * speeds[i] * 0.7 + phases[i]) * 0.4);
        const h = 6 + v * (maxHeight - 6) * amplitude;
        s.style.height = `${h}px`;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [bars, maxHeight, amplitude, speed]);

  return (
    <div ref={ref} className={`flex items-end gap-1.5 ${className}`} style={{ height: maxHeight }}>
      {Array.from({ length: bars }).map((_, i) => (
        <span
          key={`eq-bar-${i}`}
          data-bar="1"
          className="block w-[6px] sm:w-[8px] transition-colors duration-300 hover:bg-[var(--magenta)]"
          style={{ height: 8, backgroundColor: color }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = color)}
        />
      ))}
    </div>
  );
}

// helper hook for scroll progress within ref
function useScrollProgress(ref) {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const total = ref.current.offsetHeight - window.innerHeight;
      const passed = -rect.top;
      setP(Math.min(1, Math.max(0, passed / total)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [ref]);
  return p;
}

/* ──────────────────────────────────────────── MOBILE CONTACT SHEET
   Native horizontal swipe (CSS scroll-snap) — no scroll-jacking. Cards in 4:5 vertical
   for portrait-friendly mobile reading. Auto-shown only below md breakpoint. */
function MobileContactSheet({ projects }) {
  const [filter, setFilter] = useState("ALL");
  const accentByCategory = { musica: "var(--magenta)", eventos: "var(--tungsten)", viajes: "var(--cyan)", retratos: "var(--rose)" };

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    if (!f || !f.cat) return projects.slice(0, 10);
    return projects.filter((p) => p.category === f.cat);
  }, [filter, projects]);

  return (
    <section
      data-testid="mobile-contact-sheet"
      className="md:hidden bg-black px-5 pt-[14vh] pb-12"
    >
      {/* Header chip */}
      <div className="inline-flex items-center gap-3 bg-black/60 border border-white/[0.08] px-3 py-2 mb-5">
        <ApertureIcon size={12} />
        <p className="font-mono text-[10px] tracking-[0.3em] text-white/75">
          {filter === "ALL"
            ? `${String(filtered.length).padStart(2, "0")} DESTACADAS · DE ${String(projects.length).padStart(2, "0")}`
            : `${String(filtered.length).padStart(2, "0")} · ${FILTERS.find(f=>f.key===filter)?.label.toUpperCase()}`}
        </p>
      </div>

      {/* Hint */}
      <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3 flex items-center gap-2">
        <span>Desliza →</span>
        <span className="block h-px flex-1 bg-white/15" />
      </p>

      {/* Filter pills — horizontal scroll */}
      <div data-testid="mobile-filter" className="overflow-x-auto -mx-5 px-5 mb-6 scrollbar-hide">
        <div className="flex gap-2 w-max">
          {FILTERS.map((f) => {
            const isActive = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`mobile-filter-${f.key.toLowerCase()}`}
                className={`shrink-0 font-display uppercase tracking-[0.22em] text-xs px-4 py-2 border transition-colors ${
                  isActive ? "text-white border-[var(--magenta)] bg-[var(--magenta)]/10" : "text-white/55 border-white/15 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Cards — horizontal snap swipe, 4:5 vertical aspect */}
      <div
        className="overflow-x-auto snap-x snap-mandatory -mx-5 px-5 flex gap-4 pb-4 scrollbar-hide"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* Lead title card — same statement as desktop, scaled for mobile */}
        <div
          data-testid="mobile-title-card"
          className="snap-center shrink-0 flex flex-col justify-center"
          style={{ width: "82vw", maxWidth: 520, aspectRatio: "4 / 5" }}
        >
          <p className="font-mono text-[10px] tracking-[0.4em] text-[var(--magenta)] mb-4">
            00 / ARCHIVO · {(filter === "ALL" ? "DESTACADAS" : FILTERS.find(f=>f.key===filter)?.label || "").toUpperCase()}
          </p>
          <h1 className="font-display uppercase font-bold tracking-tighter leading-[0.88] text-white text-[12vw]">
            Fotografía
            <br />
            para
            <br />
            <span className="text-[var(--magenta)]">artistas</span>
            <br />
            y creadores.
          </h1>
          <p className="mt-6 font-mono text-[10px] tracking-[0.3em] uppercase text-white/45">
            Desliza →
          </p>
        </div>

        {filtered.map((p, idx) => {
          const accent = accentByCategory[p.category] || "var(--magenta)";
          return (
            <Link
              key={p.slug}
              to={`/proyecto/${p.slug}`}
              data-testid={`mobile-frame-${p.slug}`}
              className="snap-center shrink-0 flex flex-col"
              style={{ width: "82vw", maxWidth: 520 }}
            >
              <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.3em] text-white/40 mb-2 px-0.5">
                <span>{String(idx + 1).padStart(2, "0")} / {String(filtered.length).padStart(2, "0")}</span>
                <span style={{ color: accent }}>{p.category.toUpperCase()}</span>
              </div>
              <div
                className="relative overflow-hidden bg-[var(--surface)]"
                style={{ aspectRatio: "4 / 5" }}
              >
                <img
                  src={resolveImg(p.cover)}
                  alt={`${p.title} — fotografía de ${p.category} por Emmanuel Upegui`}
                  loading={idx === 0 ? "eager" : "lazy"}
                  decoding="async"
                  fetchPriority={idx === 0 ? "high" : "auto"}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{
                    filter: "contrast(1.05) brightness(0.97)",
                    objectPosition: `${p.cover_focus_x ?? 50}% ${p.cover_focus_y ?? 50}%`,
                  }}
                />
                <CornerTicks />
                <div
                  className="absolute left-0 top-0 bottom-0 w-[3px]"
                  style={{ backgroundColor: accent, boxShadow: `0 0 18px ${accent}` }}
                />
              </div>
              <p className="font-display uppercase tracking-tight text-2xl leading-[0.95] text-white mt-3 px-0.5">
                {p.title}
              </p>
            </Link>
          );
        })}
      </div>

      {/* When ALL is active, hint that there are more */}
      {filter === "ALL" && projects.length > filtered.length && (
        <p className="mt-6 text-center font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
          {projects.length - filtered.length} series más en cada categoría
        </p>
      )}
    </section>
  );
}

/* ──────────────────────────────────────────── VOLUME OUTRO — closing line */
function VolumeOutro() {
  return (
    <section
      data-testid="volume-outro"
      className="relative bg-black border-t border-white/5 overflow-hidden"
    >
      <div className="max-w-[1500px] mx-auto px-6 sm:px-12 lg:px-24 py-32 sm:py-44">
        <div className="flex items-center gap-3">
          <RecDot />
          <p className="font-mono text-[10px] tracking-[0.4em] text-white/30">FADE TO BLACK</p>
        </div>
        <h2 className="mt-10 font-display uppercase font-light tracking-tighter leading-[0.9] text-[12vw] sm:text-[10vw] lg:text-[8.2vw]">
          ¿Puedes
          <span className="block text-[var(--magenta)] font-medium">subir el volumen</span>
          tan solo un poco?
        </h2>
        {/* Animated equalizer — 56 bars dancing forever */}
        <div className="mt-14" aria-hidden>
          <Equalizer bars={56} maxHeight={88} amplitude={1} speed={1.4} />
        </div>
        <p className="mt-10 max-w-2xl font-serif-accent text-base sm:text-lg leading-relaxed text-white/55 font-light">
          Música, eventos, viajes — un mismo oficio: ayudar a desarrollar la voz del artista,
          tender puentes, establecer escenarios para el encuentro entre la diferencia.
        </p>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────── EPILOGUE — SEO content block */
/* ──────────────────────────────────────────── EPILOGUE — services as a visual mural */
const SERVICES = [
  { icon: Mic2,       title: "Sesiones de banda",      blurb: "Promo, álbum, identidad visual. Para artistas que necesitan una imagen tan fuerte como su sonido.", accent: "var(--magenta)" },
  { icon: Music,      title: "Conciertos y festivales", blurb: "Cobertura editorial pensada para prensa, sello y archivo. Energía de tarima sin perder la mirada.", accent: "var(--cyan)" },
  { icon: Newspaper,  title: "Reportaje editorial",     blurb: "Encargos para revistas, medios y publicaciones. Foto y texto cuando la historia lo pide.", accent: "var(--tungsten)" },
  { icon: BookOpen,   title: "Cultura larga duración",  blurb: "Proyectos documentales que toman semanas o años. La cultura se cuenta despacio.", accent: "var(--rose)" },
  { icon: Heart,      title: "Eventos privados",        blurb: "Bodas, ceremonias, momentos íntimos. Mirada documental sin guion previo.", accent: "var(--magenta)" },
  { icon: Camera,     title: "Comisiones de marca",     blurb: "Para sellos, instituciones culturales y marcas que cuidan su narrativa visual.", accent: "var(--cyan)" },
  { icon: Frame,      title: "Prints fine-art",         blurb: "Copias numeradas y firmadas en Hahnemühle o Baryta. Envío internacional.", accent: "var(--tungsten)" },
  { icon: Plane,      title: "Trabajo en gira",         blurb: "Disponible para Latinoamérica, Europa y Asia. Pasaporte siempre listo.", accent: "var(--rose)" },
];

function ServiceCard({ icon: Icon, title, blurb, accent }) {
  return (
    <div
      data-testid={`service-${title.toLowerCase().replace(/\s+/g,"-")}`}
      className="group relative bg-black/40 border border-white/[0.07] p-7 lg:p-8 overflow-hidden transition-colors duration-500 hover:border-white/0 cursor-default"
      style={{ "--svc-accent": accent }}
    >
      {/* Color sweep that enters from below on hover */}
      <span
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{ background: `linear-gradient(180deg, transparent 0%, ${accent}15 100%)` }}
      />
      {/* Top-left index numeral fades in on hover for that magazine vibe */}
      <span className="absolute top-3 right-4 font-mono text-[10px] tracking-[0.3em] text-white/20 group-hover:text-[color:var(--svc-accent)] transition-colors duration-500">
        ✦
      </span>
      {/* Icon — bordered square, scales + rotates lightly on hover */}
      <span
        className="relative inline-flex items-center justify-center w-12 h-12 border border-white/15 transition-all duration-500 group-hover:border-[color:var(--svc-accent)] group-hover:rotate-[-6deg] group-hover:scale-110"
        style={{ boxShadow: `0 0 0 0 ${accent}` }}
      >
        <Icon size={20} className="text-white/85 transition-colors duration-500 group-hover:text-[color:var(--svc-accent)]" strokeWidth={1.4} />
      </span>
      <h3 className="relative font-display uppercase tracking-tight text-xl lg:text-2xl mt-6 leading-tight">
        {title}
      </h3>
      <p className="relative font-serif-accent text-white/65 mt-3 text-[15px] leading-relaxed font-light">
        {blurb}
      </p>
      {/* Bottom accent line that expands on hover */}
      <span
        className="absolute left-0 bottom-0 h-[2px] w-0 group-hover:w-full transition-[width] duration-700 ease-out"
        style={{ backgroundColor: accent }}
      />
    </div>
  );
}

function Epilogue() {
  return (
    <section className="relative bg-black border-t border-white/5">
      <div className="max-w-[1500px] mx-auto px-6 sm:px-12 lg:px-24 py-28 sm:py-36">
        <div className="grid grid-cols-12 gap-8 mb-12 sm:mb-16">
          <div className="col-span-12 lg:col-span-5">
            <p className="font-mono text-[10px] tracking-[0.4em] text-[var(--cyan)]">SERVICIOS · 2026</p>
            <h2 className="mt-5 font-display uppercase font-medium tracking-tighter leading-[0.95] text-3xl sm:text-5xl lg:text-[3.6rem]">
              Para bandas, festivales,
              <br />
              <span className="text-white/55">marcas y publicaciones.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 self-end">
            <p className="font-serif-accent text-lg sm:text-xl text-white/70 font-light leading-[1.55]">
              Ocho frentes de trabajo, una misma búsqueda: contar bien la historia. Pasea el cursor por
              cada uno para descubrirlos.
            </p>
          </div>
        </div>

        {/* SERVICE GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {SERVICES.map((s) => <ServiceCard key={s.title} {...s} />)}
        </div>

        <div className="flex flex-wrap gap-4 pt-14">
          <Link
            to="/sobre-mi#contacto"
            data-testid="epilogue-contact"
            className="border border-white/20 hover:border-[var(--magenta)] hover:text-[var(--magenta)] px-7 py-4 text-[11px] tracking-[0.3em] uppercase font-display transition-colors"
          >
            Solicitar sesión
          </Link>
          <Link
            to="/sobre-mi"
            data-testid="epilogue-about"
            className="inline-flex items-center gap-3 px-7 py-4 text-[11px] tracking-[0.3em] uppercase font-display text-white/70 hover:text-white"
          >
            Conocer al fotógrafo <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────── HOME */
export default function Home() {
  const [projects, setProjects] = useState([]);
  const [showCold, setShowCold] = useState(() => {
    if (typeof window === "undefined") return true;
    return sessionStorage.getItem("eupegui-cold-open") !== "done";
  });

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const enter = () => {
    sessionStorage.setItem("eupegui-cold-open", "done");
    setShowCold(false);
  };

  return (
    <div data-testid="home-page" className="bg-black">
      <Seo
        title="Fotógrafo de la cultura — Música, eventos, viajes y retratos"
        description="Portafolio cinemático de Emmanuel Upegui: 15+ años fotografiando proyectos musicales, artísticos y culturales en Colombia, México, Perú, España, Estados Unidos, Alemania y China."
        path="/"
        keywords="fotógrafo de música, fotografía documental, conciertos, festivales, retratos, Emmanuel Upegui, eupegui, Colombia"
      />
      <AnimatePresence>{showCold && <ColdOpen onEnter={enter} />}</AnimatePresence>

      {projects.length > 0 ? (
        <>
          <ContactSheet projects={projects} />
          <MobileContactSheet projects={projects} />
        </>
      ) : (
        <div className="h-screen flex items-center justify-center text-white/40 font-mono text-xs tracking-widest">
          CARGANDO ARCHIVO…
        </div>
      )}

      <VolumeOutro />
      <Epilogue />
    </div>
  );
}
